import React from 'react';
import styled from 'styled-components';
import {
  BORDER_SECONDARY,
  LIGHT_GRAY,
  ORANGE_RAMP_300,
  ORANGE_RAMP_500,
  ORANGE_RAMP_700,
  ORANGE_RAMP_900,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../../config/Constants';
import { CalcFoundationPentagonAxis, CalcFoundationPentagonUnit } from '../../types/calcFoundationPentagon';

// 計算力の土台の「単元ごとのタイムグラフ」。
// 計算力レポート（印刷）と同じ見方を画面にも出すためのコンポーネントで、
// 分野ごとに単元を並べ、ミスなし最速タイムを横棒で、目標タイム（S / A / A×1.5）を縦線で示す。
// 集計はサーバー（CalcFoundationPentagonEvaluator）側で完結している。

type Props = {
  axes: CalcFoundationPentagonAxis[];
};

// 目標タイムの区切り線の色。下地（薄グレー）の上でも棒の上でも見えるよう半透明の濃いグレーにする。
const GOAL_LINE_COLOR = 'rgba(37, 41, 46, 0.45)';

// 横軸の上限（秒）。これを超えるタイムは頭打ちで表示する。
const BAR_MAX_TIME_SEC = 600;
// 横軸の目盛り（秒）。
const SCALE_TICKS_SEC = [0, 120, 240, 360, 480, 600];

// 単元スコア（0〜4点）に対応する棒の色。
// 4点=目標S / 3点=目標A / 2点=A×1.5以内 / 1点=満点ありタイム未達。0点は棒を描かない。
//
// グレードは順序のあるデータなので、色相を変えず明度だけを4段階にした単色ランプにする
// （濃い＝良い）。$orange-primary #e96712 をランプの1段として使う。
// メダル配色（金/銀/銅）は明度が順序どおりに並ばず、色から順序が読めないため採用しない。
// 印刷レポート（calc_power_reports）も同じランプで塗る。色を変えるときは両方を揃える。
const BAR_COLOR_BY_SCORE: Record<number, string> = {
  4: ORANGE_RAMP_900,
  3: ORANGE_RAMP_700,
  2: ORANGE_RAMP_500,
  1: ORANGE_RAMP_300,
};

// 単元スコアに対応する最速タイム欄のグレード記号。
// 2点は「目標タイムA×1.5以内」だが、記号は B の1文字にする。
// 「A×1.5」と出すと最速タイム欄が広がり、そのぶん棒が短くなって見くらべにくくなるため。
// A×1.5 との対応は凡例（B(A×1.5) / C(A×1.5超)）で示す。印刷レポートの calc_power_grade_mark と揃える。
const GRADE_MARK_BY_SCORE: Record<number, string> = {
  4: 'S',
  3: 'A',
  2: 'B',
  1: 'C',
};

const formatTime = (sec: number): string => `${Math.floor(sec / 60)}分${String(sec % 60).padStart(2, '0')}秒`;

const scaleTickLabel = (sec: number): string => (sec === 0 ? '0' : `${sec / 60}分`);

// 横軸上の位置（%）。上限を超える値は100%で頭打ちにする。
const barRatio = (sec: number): number => Math.min((sec / BAR_MAX_TIME_SEC) * 100, 100);

export const CalcFoundationUnitTimeChart: React.FC<Props> = ({ axes }) => {
  return (
    <DivWrap>
      <DivLegend>
        <span>ミスなし最速タイム：</span>
        <SpanLegendItem>
          <IMark style={{ backgroundColor: BAR_COLOR_BY_SCORE[4] }} />
          目標S
        </SpanLegendItem>
        <SpanLegendItem>
          <IMark style={{ backgroundColor: BAR_COLOR_BY_SCORE[3] }} />
          目標A
        </SpanLegendItem>
        <SpanLegendItem>
          <IMark style={{ backgroundColor: BAR_COLOR_BY_SCORE[2] }} />
          B(A×1.5)
        </SpanLegendItem>
        <SpanLegendItem>
          <IMark style={{ backgroundColor: BAR_COLOR_BY_SCORE[1] }} />
          C(A×1.5超)
        </SpanLegendItem>
        <span>／ 縦線＝目標タイムの位置</span>
      </DivLegend>

      <DivScaleRow>
        <SpanUnitTitle>単元</SpanUnitTitle>
        <DivScaleInner>
          {SCALE_TICKS_SEC.map((sec, index) => (
            <SpanScaleTick
              key={sec}
              isFirst={index === 0}
              isLast={index === SCALE_TICKS_SEC.length - 1}
              style={{ left: `${barRatio(sec)}%` }}
            >
              {scaleTickLabel(sec)}
            </SpanScaleTick>
          ))}
        </DivScaleInner>
        <SpanRecord>最速タイム</SpanRecord>
      </DivScaleRow>

      {axes.map((axis) => (
        <DivAxisGroup key={axis.axisKey}>
          <DivAxisHead>
            <span>{axis.axisLabel}</span>
            <SpanAxisScore>
              {axis.score.toFixed(1)} / {axis.maxScore}点
            </SpanAxisScore>
          </DivAxisHead>
          {axis.units.map((unit) => (
            <UnitRow key={unit.questionSetId} unit={unit} />
          ))}
        </DivAxisGroup>
      ))}
    </DivWrap>
  );
};

const UnitRow: React.FC<{ unit: CalcFoundationPentagonUnit }> = ({ unit }) => {
  const goalTimes = [unit.sGradeTimeSec, unit.aGradeTimeSec, unit.aGradeTimeSec == null ? null : unit.aGradeTimeSec * 1.5];
  const barColor = BAR_COLOR_BY_SCORE[unit.score];

  return (
    <DivUnitRow>
      <SpanUnitTitle>{unit.title}</SpanUnitTitle>
      <DivBarArea>
        <DivBarBg>
          {unit.bestTimeSec != null && barColor != null && (
            <SpanBarFill style={{ width: `${barRatio(unit.bestTimeSec)}%`, backgroundColor: barColor }} />
          )}
          {/* 目標タイムの区切りは塗りより後に描く。塗りの上では白い切れ目、
              空きの上ではグレーの縁として見えるので、色が付いていても境目が分かる */}
          {goalTimes.map((goalSec, index) =>
            goalSec == null ? null : <SpanGoalLine key={index} style={{ left: `${barRatio(goalSec)}%` }} />,
          )}
        </DivBarBg>
      </DivBarArea>
      {unit.bestTimeSec == null ? (
        <SpanRecord isNoRecord>記録なし</SpanRecord>
      ) : (
        <SpanRecord>
          <b>{formatTime(unit.bestTimeSec)}</b> {GRADE_MARK_BY_SCORE[unit.score] ?? ''}
        </SpanRecord>
      )}
    </DivUnitRow>
  );
};

const DivWrap = styled.div`
  padding: 0.25rem 0.5rem;
`;

const DivLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.75rem;
  font-size: 0.6875rem;
  color: ${TEXT_SECONDARY};
  margin-bottom: 0.25rem;
`;

const SpanLegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const IMark = styled.i`
  display: inline-block;
  width: 9px;
  height: 9px;
`;

const DivScaleRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.625rem;
  color: ${TEXT_SECONDARY};
`;

// 目盛りは棒と同じ位置に % 配置する
const DivScaleInner = styled.div`
  flex: 1;
  position: relative;
  height: 0.75rem;
  padding: 0 4px;
`;

const SpanScaleTick = styled.span<{ isFirst: boolean; isLast: boolean }>`
  position: absolute;
  top: 0;
  white-space: nowrap;
  transform: ${(p) => (p.isFirst ? 'none' : p.isLast ? 'translateX(-100%)' : 'translateX(-50%)')};
`;

const DivAxisGroup = styled.div`
  margin-bottom: 0.5rem;
`;

const DivAxisHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 0.8125rem;
  font-weight: bold;
  color: ${TEXT_PRIMARY};
  background: ${LIGHT_GRAY};
  border-left: 3px solid ${TEXT_PRIMARY};
  padding: 0.125rem 0.5rem;
  margin-bottom: 0.125rem;
`;

const SpanAxisScore = styled.span`
  font-size: 0.75rem;
  font-weight: normal;
  color: ${TEXT_SECONDARY};
`;

const DivUnitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.125rem 0;
  border-bottom: 1px dotted ${BORDER_SECONDARY};
  font-size: 0.6875rem;
`;

// 狭い画面（ページ枠が 30rem のままの 768px 未満）では棒の幅を確保するため列を詰める。
const NARROW_MAX_WIDTH_PX = 767;

// 単元名の列。
const SpanUnitTitle = styled.span`
  flex: 0 0 9rem;
  color: ${TEXT_SECONDARY};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${NARROW_MAX_WIDTH_PX}px) {
    flex-basis: 7rem;
  }
`;

const DivBarArea = styled.div`
  flex: 1;
  padding: 0 4px;
`;

const DivBarBg = styled.div`
  position: relative;
  height: 10px;
  background: #fafafa;
  border: 1px solid ${BORDER_SECONDARY};
  box-sizing: border-box;
`;

// 目標タイムの区切り。塗りの上に半透明の濃いグレーを重ねる。
// 白1pxだと下地（薄グレー）の上で消えてしまうため、下地でも棒の上でも見える1色にする。
const SpanGoalLine = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px solid ${GOAL_LINE_COLOR};
`;

const SpanBarFill = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
`;

const SpanRecord = styled.span<{ isNoRecord?: boolean }>`
  flex: 0 0 5.5rem;

  @media (max-width: ${NARROW_MAX_WIDTH_PX}px) {
    flex-basis: 4.75rem;
  }

  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.isNoRecord ? '#aaa' : TEXT_PRIMARY)};
`;
